'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAskEllieSidebarStore } from '@/state/askEllieSidebarState';
import { useHasMounted } from '@/hooks/useHasMounted';

interface ManagerChatSectionProps {
  isCollapsed: boolean;
}

export function ManagerChatSection({ isCollapsed }: ManagerChatSectionProps) {
  const pathname = usePathname();
  const isAskElliePage = pathname?.startsWith('/ask-ellie');
  const isSidebarOpen = useAskEllieSidebarStore((s) => s.isOpen);
  const hasMounted = useHasMounted();

  // Active when on the full-page Ask Ellie route OR when the sidebar is open (after mount)
  const isActive = isAskElliePage || (hasMounted && isSidebarOpen);

  // Left nav always navigates to the full-page /ask-ellie.
  // The sidebar panel is opened only from the top-right corner trigger.

  const buttonContent = (
    <Link
      href="/ask-ellie"
      className={cn(
        'flex items-center w-full h-9 overflow-hidden rounded-lg transition-colors duration-150',
        !isCollapsed &&
          (isActive
            ? 'bg-ds-brand-lightest border border-ds-brand-subtlest'
            : 'border border-transparent hover:bg-ds-gray-subtlest')
      )}
      aria-label={isCollapsed ? 'Ask Ellie' : undefined}
    >
      <div className="flex items-center justify-center shrink-0 w-12 h-9">
        <div
          className={cn(
            'flex items-center justify-center size-9 rounded-lg transition-colors duration-150',
            isCollapsed &&
              isActive &&
              'bg-ds-brand-lightest border border-ds-brand-subtlest',
            isCollapsed && !isActive && 'hover:bg-ds-gray-subtlest'
          )}
        >
          <Image
            src="/assets/ellie.png"
            alt="Ellie"
            width={16}
            height={16}
            className="size-4 shrink-0 object-cover rounded-full"
          />
        </div>
      </div>
      <div
        className={cn(
          'flex items-center gap-2 whitespace-nowrap overflow-hidden transition-[max-width,opacity] duration-200 ease-out',
          isCollapsed ? 'max-w-0 opacity-0' : 'max-w-[180px] opacity-100 pr-2'
        )}
      >
        <span
          className={cn(
            'font-[500] leading-[18px] font-sf-pro-text text-sm',
            isActive ? 'text-ds-brand-default' : 'text-ds-text-subtle'
          )}
        >
          Ask Ellie
        </span>
      </div>
    </Link>
  );

  return (
    <div className="space-y-0">
      {isCollapsed ? (
        <TooltipProvider delay={200}>
          <Tooltip>
            <TooltipTrigger render={buttonContent} />
            <TooltipContent side="right" align="center">
              Ask Ellie
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        buttonContent
      )}
    </div>
  );
}
